import React, { useState, useEffect, useMemo } from 'react';
import { Activity, Map, Search, Info, X, Loader, Zap, CheckCircle, AlertTriangle, Languages, Globe, MapPin,ChevronDown,
         Server, Clock, Timer, Package, Network, Home, Wifi, Monitor, Cpu, Hash,  Building,Scan, HardDrive, AlertCircle,
         RefreshCw, DownloadCloud, Upload, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import PingAnalys from './ping';
import TracerouteVisualization from './Traceroute';
import NetworkConnections from './NetworkConnections';
import NetworkInterfaces from './interfaces';
import NetworkUsage from './NetworkUsage';

const NetworkDiagnostics = () => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [target, setTarget] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('connections');
  const [visibleConnections, setVisibleConnections] = useState(50);

  const tools = [
    {
        id: 'ping',
        name: 'Connection Test',
        icon: <Activity className="h-5 w-5" />,
        description: 'Check if a website or server is responding',
        placeholder: 'Enter website (e.g., google.com) or IP address',
    },
    {
        id: 'traceroute',
        name: 'Route Mapper',
        icon: <Map className="h-5 w-5" />,
        description: 'See the path your connection takes to reach a destination',
        placeholder: 'Enter destination to map the route',
    },
    {
        id: 'dns',
        name: 'Name Lookup',
        icon: <Search className="h-5 w-5" />,
        description: 'Convert website names to IP addresses and vice versa',
        placeholder: 'Enter domain name or IP address',
    },
    {
        id: 'network-info',
        name: 'Network Info',
        icon: <Info className="h-5 w-5" />, 
        description: 'Get the IP and MAC address of your computer',
        placeholder: 'No input required for this tool, type anything to run',      
    },
  
    {
      id: 'nmap_scan',
      name: 'Nmap Scan',
      icon: <Scan className="h-5 w-5" />, // You can use a relevant scan icon
      description: 'Perform a network scan on the given IP address or domain name',
      placeholder: 'Enter IP address or domain name for scan',
  }
];

// Update the reducer to handle the new tool results
const reducer = (state, action) => {
  switch (action.type) {
      case 'SET_SELECTED_TOOL':
          return { ...state, selectedTool: action.selectedTool };
      case 'UPDATE_TARGET':
          return { ...state, target: action.target };
      case 'SET_TOOL_RESULTS':
          return { ...state, toolResults: action.toolResults };
      case 'SET_ERROR':
          return { ...state, error: action.error };
      case 'TOOL_CHANGE':
          return { ...state, target: '', toolResults: null, error: null };
      case 'SET_IS_RUNNING':
          return { ...state, isRunning: action.isRunning };
      default:
          return state; // Always return the current state for unrecognized actions
  }
};

// Initial state
const initialState = {
  selectedTool: null,
  toolResults: null,
  target: '',
  error: null,
  isRunning: false,
};

// Now we can use useReducer
const [state, dispatch] = React.useReducer(reducer, initialState);

// Input validation function
const validateInput = (input, tool) => {
  const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  const ipRegex = /^(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

  // Validate based on selected tool
  switch (tool.name) {
      case 'Connection Test':
      case 'Route Mapper':
      case 'Name Lookup':
          return urlRegex.test(input) || ipRegex.test(input);
      case 'Network Info':
      case 'Network Connections':
          return true; // No input required
      case 'Nmap Scan':
          return urlRegex.test(input) || ipRegex.test(input); // Require IP or domain
      default:
          return false;
  }
};

// Helper function to format bytes
function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// Function to handle tool change
const handleToolChange = (tool) => {
  // Clear results and error when changing tools
  dispatch({ type: 'TOOL_CHANGE' });
  dispatch({ type: 'SET_SELECTED_TOOL', selectedTool: tool });

  // Handle specific tool requests
  if (tool.name === 'Network Info') {    
      // Directly send a request for Network Info tool
      dispatch({ type: 'SET_TOOL_RESULTS', toolResults: { type: 'network-info', data: 'Network Data...' } });
  } else if (tool.name === 'Network Connections') { 
      fetchNetstatResults();
  } else if (tool.name === 'Nmap Scan') { 
      // Handle Nmap Scan request with the current target
      if (state.target) {
          fetchNmapScanResults(state.target);
      }
  }
};

// Function to fetch Nmap scan results from the backend
const fetchNmapScanResults = async (target) => {
  dispatch({ type: 'SET_IS_RUNNING', isRunning: true }); // Set the loading state
  try {
      const response = await fetch(`/nmap_scan?target=${encodeURIComponent(target)}`); // Adjust the endpoint if necessary
      if (!response.ok) {
          throw new Error(`Error fetching Nmap scan data: ${response.statusText}`);
      }

      const data = await response.json();
      dispatch({ type: 'SET_TOOL_RESULTS', toolResults: data });
  } catch (error) {
      dispatch({ type: 'SET_ERROR', error: error.message });
  } finally {
      dispatch({ type: 'SET_IS_RUNNING', isRunning: false }); // Reset loading state
  }
};

// Function to fetch netstat results from the backend
// Function to fetch netstat results from the backend
const fetchNetstatResults = async () => {
  dispatch({ type: 'SET_IS_RUNNING', isRunning: true }); // Set the loading state
  try {
      const response = await fetch('/v1/netstat'); // Adjust the endpoint as necessary
      if (!response.ok) {
          throw new Error(`Error fetching netstat data: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Check if the data matches the expected structure
      if (!data.connections || !Array.isArray(data.connections)) {
          throw new Error('Invalid data structure returned for netstat results.');
      }

      //console.log(data.connections)

      // Format the Netstat results directly within this function
      const formattedResults = {
          connections: data.connections.map(connection => ({
              protocol: connection.protocol,
              local_address: connection.local_address,
              remote_address: connection.remote_address,
              state: connection.state,
              pid: connection.pid,
              process_name: connection.process_name,
          })),
          total_connections: data.total_connections,
          network_interfaces: data.network_interfaces,
          network_usage: data.network_usage,
          open_ports: data.open_ports,
          internet_status: data.internet_status,
      };

      // Handle the results for "Network Connections"
      setResults({
          type: 'netstat',
          data: formattedResults,
      });

      console.log("results ", results.type)
      dispatch({ type: 'SET_TOOL_RESULTS', toolResults: formattedResults });
  } catch (error) {
      dispatch({ type: 'SET_ERROR', error: error.message });
  } finally {
      dispatch({ type: 'SET_IS_RUNNING', isRunning: false }); // Reset loading state
  }
};




// Function to handle input change (if still needed for other tools)
const handleInputChange = (target) => {
  dispatch({ type: 'UPDATE_TARGET', target });
};

// Function to determine the endpoint based on selected tool
const getEndpoint = (tool) => {
  switch (tool.name) {
      case 'Connection Test':
          return '/v1/ping';
      case 'Route Mapper':
          return '/v1/traceroute';
      case 'Name Lookup':
          return '/v1/name-lookup';
      case 'Network Info':
          return '/v1/network-info';
      case 'Network Connections': // Added case for netstat
          return '/v1/netstat';
      case 'Nmap Scan': // Added case for Nmap Scan
          return '/v1/nmap_scan'; // Ensure this matches your backend API route
      default:
          return null;
  }
};

  const sendRequest = () => {
      dispatch({ type: 'SET_IS_RUNNING', isRunning: true });
      const selectedTool = state.selectedTool;

      // Check if the selected tool is "Network Info"
      if (selectedTool.name === 'Network Info') {
          const endpoint = getEndpoint(selectedTool);
          if (!endpoint) {
              const errorMsg = 'No endpoint found for the selected tool.';
              alert(errorMsg);
              dispatch({ type: 'SET_ERROR', error: errorMsg });
              dispatch({ type: 'SET_IS_RUNNING', isRunning: false });
              return;
          }

          // Directly fetch data from the "Network Info" endpoint
          fetch(endpoint)
              .then(response => {
                  if (!response.ok) {
                      throw new Error('Network response was not ok');
                  }
                  return response.json();
              })
              .then(toolResults => {
                  // Handle the results for "Network Info"
                  setResults({
                      type: 'network-info',
                      data: toolResults,
                  });
                  dispatch({ type: 'SET_TOOL_RESULTS', toolResults });
                  dispatch({ type: 'SET_IS_RUNNING', isRunning: false });
              })
              .catch(error => {
                  const errorMsg = 'Failed to retrieve data: ' + error.message;
                  alert(errorMsg);
                  dispatch({ type: 'SET_ERROR', error: errorMsg });
                  dispatch({ type: 'SET_IS_RUNNING', isRunning: false });
              });

          return;
      }
      

      // For other tools, continue with input validation
      const input = state.target;

      // Validate input
      if (!validateInput(input, selectedTool)) {
          const errorMsg = 'Invalid input. Please enter a valid URL or IP address.';
          alert(errorMsg);
          dispatch({ type: 'SET_ERROR', error: errorMsg });
          dispatch({ type: 'SET_IS_RUNNING', isRunning: false });
          return;
      }

      const endpoint = getEndpoint(selectedTool);
      if (!endpoint) {
          const errorMsg = 'No endpoint found for the selected tool.';
          alert(errorMsg);
          dispatch({ type: 'SET_ERROR', error: errorMsg });
          dispatch({ type: 'SET_IS_RUNNING', isRunning: false });
          return;
      }

      // Prepare the data to be sent in the POST request body
      const requestData = { target: input };

      // Fetch data from the determined endpoint using POST with body
      fetch(endpoint, {
          method: 'POST',
          headers: { 
              'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
      })
      .then(response => {
          if (!response.ok) {
              throw new Error('Network response was not ok');
          }
          return response.json();
      })
      .then(toolResults => {
          // Set the results in the local component state based on tool type
          let formattedResults;

          switch (selectedTool.name) {
              case 'Connection Test':
                  formattedResults = {
                      type: 'ping',
                      target: input,
                      status: toolResults.status,
                      resolved_ip: toolResults.resolved_ip,
                      bytes: toolResults.bytes,
                      packets: {
                          sent: toolResults.packets.sent,
                          received: toolResults.packets.received,
                          lost: toolResults.packets.lost,
                      },
                      latency: {
                          min: toolResults.latency.min,
                          max: toolResults.latency.max,
                          average: toolResults.latency.average,
                      },
                      ttl: toolResults.ttl,
                  };
                  break;

              case 'Route Mapper':
                  formattedResults = {
                      type: 'traceroute',
                      target: input,
                      hops: toolResults.hops,
                  };
                  break;

              case 'Name Lookup':
                  formattedResults = {
                      type: 'name_lookup',
                      target: input,
                      domain: toolResults.domain,
                      host_name: toolResults.host_name,
                      ip_address: toolResults.ip_address,
                      aliases: toolResults.aliases,
                  };
                  break;

              case 'Network Info':
                                  formattedResults = {
                                    type: 'network-info',
                                    client_ip: toolResults.client_ip,
                                    os: toolResults.os,
                                    browser: toolResults.browser,
                                    accept_language: toolResults.accept_language,
                                    geo_info: toolResults.geo_info,
                                   
                                    public_ip: toolResults.client_ip, 
                                    platform: toolResults.os 
                                  };
                                  
                                  break;

                 case 'Nmap Scan':
                    formattedResults = {
                      type: 'nmap',
                      target: input,
                      resolved_ip: toolResults.resolved_ip,
                      scan_results: {
                        host_status: toolResults.scan_results.host_status,
                        device_vendor: toolResults.scan_results.device_vendor,
                        latency: toolResults.scan_results.latency,
                        mac_address: toolResults.scan_results.mac_address,
                        os: toolResults.scan_results.os,
                        ports: toolResults.scan_results.ports,
                        vulnerabilities: toolResults.scan_results.vulnerabilities || []
                      }
                    };
                    break;

              default:
                  formattedResults = null;
          }

          if (formattedResults) {
              setResults(formattedResults);
              dispatch({ type: 'SET_TOOL_RESULTS', toolResults });
          }

          dispatch({ type: 'SET_IS_RUNNING', isRunning: false });
      })
      .catch(error => {
          const errorMsg = 'Failed to retrieve data: ' + error.message;
          alert(errorMsg);
          dispatch({ type: 'SET_ERROR', error: errorMsg });
          dispatch({ type: 'SET_IS_RUNNING', isRunning: false });
      });
  };

  
 
  // Calculate derived metrics
  const packetLossRate = results?.packets?.sent ? Math.floor((results.packets.lost / results.packets.sent) * 100) : 0;
  const responseRate = results?.packets?.sent ? Math.floor((results.packets.received / results.packets.sent) * 100) : 0;
  
  const getConnectionQuality = (latency) => {
    if (latency < 30) return { level: 'Excellent', color: 'text-emerald-400' };
    if (latency < 50) return { level: 'Very Good', color: 'text-green-400' };
    if (latency < 100) return { level: 'Good', color: 'text-blue-400' };
    if (latency < 150) return { level: 'Fair', color: 'text-yellow-400' };
    return { level: 'Poor', color: 'text-red-400' };
  };

    

  const infoItems = results && results.type === 'network-info' ? [
    {
        icon: <Monitor className="text-emerald-400" size={24} />,
        label: "Hostname",
        value: results.data.hostname || 'N/A',
        description: "Device Name"
    },
    {
        icon: <Cpu className="text-blue-400" size={24} />,
        label: "Architecture",
        value: results.data.architecture || 'N/A',
        description: "System Architecture"
    },
    {
        icon: <Server className="text-purple-400" size={24} />,
        label: "Platform",
        value: results.data.platform || 'N/A',
        description: "Operating System"
    },
    {
        icon: <Network className="text-cyan-400" size={24} />,
        label: "Local IP",
        value: results.data.local_ip || 'N/A',
        description: "Internal Network Address"
    },
    {
        icon: <Globe className="text-orange-400" size={24} />,
        label: "Public IP",
        value: results.data.public_ip || 'N/A',
        description: "External Network Address"
    },
    {
        icon: <Hash className="text-yellow-400" size={24} />,
        label: "MAC Address",
        value: results.data.mac_address || 'N/A',
        description: "Hardware Address"
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
     <div className="w-full p-6">        

        {/* Main content */}
        <div className="relative">
          {/* Background elements */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-5"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-500 rounded-full filter blur-3xl opacity-5"></div>
          
          {/* Content */}
          {!state.selectedTool ? (
            <div className="relative">
            {/* Animated background grid */}
            <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
              <div className="absolute left-0 top-0 h-full w-1/2 bg-gradient-to-r from-blue-900/50 to-transparent"></div>
            </div>
        
            <div className="relative z-10">
              
              
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                {tools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => handleToolChange(tool)}
                    className="group bg-gray-900/80 backdrop-blur-md p-6 rounded-xl border border-gray-800 hover:border-blue-400/50 
                              transition-all duration-300 text-left relative overflow-hidden hover:shadow-xl hover:shadow-blue-500/20"
                  >
                    {/* Animated connection dots */}
                    <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_8px_2px_rgba(59,130,246,0.5)]"></div>
                    
                    {/* Network connection lines animation */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute top-0 left-0 w-full h-full border-t border-l border-blue-400/20 rounded-tl-xl"></div>
                      <div className="absolute bottom-0 right-0 w-full h-full border-b border-r border-blue-400/20 rounded-br-xl"></div>
                    </div>
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-blue-900/0 opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="p-3 bg-black/40 rounded-lg text-blue-400 group-hover:text-white transition-all duration-300 group-hover:bg-blue-500/10 border border-gray-800 group-hover:border-blue-400/30">
                          {tool.icon}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white group-hover:text-blue-100 transition-colors">{tool.name}</h3>
                          <span className="text-xs text-gray-500 group-hover:text-blue-400 transition-colors">{tool.category}</span>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm mb-3">{tool.description}</p>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-500 group-hover:text-blue-400 transition-colors">Protocol: {tool.protocol}</span>
                        <span className="px-2 py-1 rounded-full bg-gray-800 text-gray-400 group-hover:bg-blue-900/30 group-hover:text-blue-400 transition-colors">
                          {tool.type}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Status bar */}
              <div className="mt-8 pt-5 border-t border-gray-800/50 flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span>System Ready</span>
                </div>
                <div>
                  <span className="font-mono">Last scan: {new Date().toLocaleTimeString()}</span>
                </div>
              </div>
            </div>
          </div>
          ) : (
            <div className="space-y-6 relative">
              {/* Network visualization background */}
              <div className="absolute inset-0 -z-10 opacity-10 pointer-events-none">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMxRTFFMUUiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyIiBjeT0iMiIgcj0iMiIvPjxjaXJjbGUgY3g9IjIiIGN5PSI1OCIgcj0iMiIvPjxjaXJjbGUgcj0iMiIgY3g9IjU4IiBjeT0iMiIvPjxjaXJjbGUgcj0iMiIgY3g9IjU4IiBjeT0iNTgiLz48L2c+PC9nPjwvc3ZnPg==')]"></div>
              </div>
          
              {/* Tool Header */}
              <div className="flex items-center justify-between bg-gray-900/80 backdrop-blur-md p-6 rounded-xl border border-gray-800 relative overflow-hidden">
                {/* Animated connection line */}
                <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-transparent w-full group-hover:w-0 transition-all duration-1000"></div>
                
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg text-blue-400 border border-blue-500/20">
                    {state.selectedTool.icon}
                  </div>
                  <div>
                    <div className="flex items-center space-x-3">
                      <h3 className="text-xl font-semibold text-white">{state.selectedTool.name}</h3>
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-400">{state.selectedTool.protocol}</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{state.selectedTool.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="hidden md:block text-xs px-3 py-1.5 rounded-full bg-gray-800/50 text-gray-400 border border-gray-700">
                    <span className="text-gray-500">Status:</span> {state.isRunning ? 'Scanning...' : 'Ready'}
                  </div>
                  <button 
                    onClick={() => {
                      handleToolChange('');
                      setResults(null);
                      setTarget('');
                    }}
                    className="p-2 hover:bg-gray-800/50 rounded-lg text-gray-400 hover:text-white transition-colors border border-gray-800 hover:border-gray-700"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
          
              {/* Input Area */}
              <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-xl border border-gray-800 relative">
                {/* Network path visualization */}
                <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>
                
                <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={state.target}
                      onChange={(e) => handleInputChange(e.target.value)}
                      placeholder={state.selectedTool.placeholder}
                      className="w-full bg-gray-900/70 border border-gray-800 rounded-lg px-4 py-3 pl-10
                                text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 
                                transition-all shadow-lg shadow-black/30 font-mono text-sm"
                    />
                    <div className="absolute left-3 top-3 text-gray-500">
                      <Network className="h-5 w-5" />
                    </div>
                  </div>
                  <button
                    onClick={sendRequest}
                    disabled={state.isRunning || (!state.target && state.selectedTool.name !== 'Network Info')}
                    className={`px-6 py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-all relative overflow-hidden
                              ${state.isRunning || (!state.target && state.selectedTool.name !== 'Network Info')
                                ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-800' 
                                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white shadow-lg hover:shadow-blue-500/30 border border-blue-700 hover:border-blue-600'}`}
                  >
                    {/* Animated pulse effect when active */}
                    {!state.isRunning && (
                      <div className="absolute inset-0 bg-blue-500/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                    )}
                    
                    {state.isRunning ? (
                      <>
                        <Loader className="h-5 w-5 animate-spin" />
                        <span className="relative">
                          Scanning
                          <span className="absolute -right-4 top-0.5 flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                          </span>
                        </span>
                      </>
                    ) : (
                      <>
                        <Zap className="h-5 w-5" />
                        <span className="relative">
                          Execute Scan
                          <span className="absolute -right-3 top-0.5 flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-0 group-hover:opacity-75 transition-opacity"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                          </span>
                        </span>
                      </>
                    )}
                  </button>
                </div>
                
                {/* Advanced options dropdown */}
                <div className="mt-4 pt-4 border-t border-gray-800/50">
                  <button className="text-xs flex items-center text-gray-500 hover:text-gray-300 transition-colors">
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Advanced parameters
                  </button>
                </div>
              </div>
        
     
         
              {/* Results Area */}
              {results && (
                <div className="bg-black bg-opacity-40 backdrop-blur-sm rounded-xl border border-gray-700 overflow-hidden">
                  <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
                    <div className="text-2xl font-bold text-white flex items-center">
                      <Activity className="mr-2 text-blue-500" size={24} />
                      Scan Results
                    </div>
                  </div>

                  <div className="p-6">
                  {results.type === 'ping' && (

                    <PingAnalys results={results} />
                    )}

                    {results.type === 'traceroute' && (
                      <TracerouteVisualization results={results} />
                    )}

                    {results.type === 'name_lookup' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between bg-gray-800 bg-opacity-70 p-6 rounded-xl shadow-lg">
                          <div className="flex items-center space-x-4">
                            <Search className="text-blue-400" size={32} />
                            <div>
                              <h1 className="text-2xl font-bold text-white">DNS Lookup Results</h1>
                              <p className="text-gray-400">Domain name resolution details</p>
                            </div>
                          </div>
                          <div className="px-4 py-2 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                            DNS Records
                          </div>
                        </div>
                  
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700">
                            <div className="flex items-start space-x-3">
                              <Globe className="text-blue-400 mt-1" size={24} />
                              <div>
                                <p className="text-sm text-gray-400">Hostname</p>
                                <p className="text-xl font-mono font-semibold text-white break-all">
                                  {results.host_name}
                                </p>
                              </div>
                            </div>
                          </div>
                  
                          <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700">
                            <div className="flex items-start space-x-3">
                              <Network className="text-emerald-400 mt-1" size={24} />
                              <div>
                                <p className="text-sm text-gray-400">IP Address</p>
                                <p className="text-xl font-mono font-semibold text-white">
                                  {results.ip_address}
                                </p>
                              </div>
                            </div>
                          </div>
                  
                          <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700">
                            <div className="flex items-start space-x-3">
                              <Server className="text-purple-400 mt-1" size={24} />
                              <div>
                                <p className="text-sm text-gray-400">Domain</p>
                                <p className="text-xl font-mono font-semibold text-white">
                                  {results.domain}
                                </p>
                              </div>
                            </div>
                          </div>
                  
                          <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700">
                            <div className="flex items-start space-x-3">
                              <Link className="text-yellow-400 mt-1" size={24} />
                              <div>
                                <p className="text-sm text-gray-400">Aliases</p>
                                <p className="text-xl font-mono font-semibold text-white">
                                  {results.aliases.length > 0 ? results.aliases.join(', ') : 'None'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

					                    {results.type === 'nmap' && (
                      <div className="space-y-6">
                        {/* Header Section */}
                        <div className="flex items-center justify-between bg-gray-800 bg-opacity-70 p-6 rounded-xl shadow-lg border border-gray-700">
                          <div className="flex items-center space-x-4">
                            <Shield className="text-purple-400" size={32} />
                            <div>
                              <h1 className="text-2xl font-bold text-white">Nmap Security Scan</h1>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-sm text-gray-400">Target:</span>
                                <span className="font-mono text-white">{results.target}</span>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  results.scan_results.host_status === 'up' 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-red-500/20 text-red-400'
                                }`}>
                                  Host {results.scan_results.host_status}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="px-4 py-2 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                            Port Scan
                          </div>
                        </div>

                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* IP Address */}
                          <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700">
                            <div className="flex items-start space-x-3">
                              <Network className="text-blue-400 mt-1" size={24} />
                              <div>
                                <p className="text-sm text-gray-400">Resolved IP</p>
                                <p className="text-xl font-mono font-semibold text-white">
                                  {results.resolved_ip}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Open Ports */}
                          <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700">
                            <div className="flex items-start space-x-3">
                              <HardDrive className="text-emerald-400 mt-1" size={24} />
                              <div>
                                <p className="text-sm text-gray-400">Open Ports</p>
                                <p className="text-xl font-semibold text-white">
                                  {results.scan_results.ports?.length || 0}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Vulnerabilities */}
                          <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700">
                            <div className="flex items-start space-x-3">
                              <AlertTriangle className="text-yellow-400 mt-1" size={24} />
                              <div>
                                <p className="text-sm text-gray-400">Vulnerabilities</p>
                                <p className="text-xl font-semibold text-white">
                                  {results.scan_results.vulnerabilities?.length || 0}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Ports Section */}
                        <div className="bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 overflow-hidden">
                          <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
                            <h2 className="text-lg font-semibold text-white flex items-center">
                              <HardDrive className="mr-2 text-emerald-400" size={20} />
                              Open Ports ({results.scan_results.ports?.length || 0})
                            </h2>
                          </div>
                          <div className="divide-y divide-gray-700">
                            {results.scan_results.ports?.map((port, index) => (
                              <div key={index} className="p-4 hover:bg-gray-800/30 transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-4">
                                    <div className="flex items-center">
                                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                                        <span className="font-mono text-emerald-400">{port.port}</span>
                                      </div>
                                    </div>
                                    <div>
                                      <p className="font-medium text-white">{port.service || 'Unknown service'}</p>
                                      <p className="text-sm text-gray-400">{port.protocol.toUpperCase()}</p>
                                    </div>
                                  </div>
                                  <div className="text-sm text-gray-400">
                                    {port.state || 'Unknown state'}
                                  </div>
                                </div>
                                {port.reason && (
                                  <div className="mt-2 text-xs text-gray-400">
                                    <span className="font-medium">Reason:</span> {port.reason}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Vulnerabilities Section */}
                        {results.scan_results.vulnerabilities?.length > 0 && (
                          <div className="bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 overflow-hidden">
                            <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
                              <h2 className="text-lg font-semibold text-white flex items-center">
                                <AlertTriangle className="mr-2 text-yellow-400" size={20} />
                                Potential Vulnerabilities ({results.scan_results.vulnerabilities.length})
                              </h2>
                            </div>
                            <div className="divide-y divide-gray-700">
                              {results.scan_results.vulnerabilities.map((vuln, index) => (
                                <div key={index} className="p-4 hover:bg-gray-800/30 transition-colors">
                                  <div className="flex items-start space-x-3">
                                    <div className={`mt-1 ${
                                      vuln.risk === 'High' ? 'text-red-400' : 
                                      vuln.risk === 'Medium' ? 'text-yellow-400' : 
                                      'text-blue-400'
                                    }`}>
                                      {vuln.risk === 'High' ? <AlertCircle size={20} /> : 
                                      vuln.risk === 'Medium' ? <AlertTriangle size={20} /> : 
                                      <Info size={20} />}
                                    </div>
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        <h3 className="font-medium text-white">{vuln.title || 'Potential vulnerability'}</h3>
                                        {vuln.port && (
                                          <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-gray-300">
                                            Port {vuln.port}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-400 mt-1">{vuln.description}</p>
                                      {vuln.recommendation && (
                                        <div className="mt-2">
                                          <p className="text-xs font-medium text-gray-400">Recommendation:</p>
                                          <p className="text-xs text-gray-300">{vuln.recommendation}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

					  {results.type === 'network-info' && (
                  <div className="space-y-6">
                    {/* Header Section */}
                    <div className="flex items-center justify-between bg-gray-800 bg-opacity-70 p-6 rounded-xl shadow-lg border border-gray-700">
                      <div className="flex items-center space-x-4">
                        <Globe className="text-blue-400" size={32} />
                        <div>
                          <h1 className="text-2xl font-bold text-white">Network Information</h1>
                          <p className="text-gray-400">Client details and geolocation data</p>
                        </div>
                      </div>
                      <div className="px-4 py-2 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        Client Scan
                      </div>
                    </div>

                    {/* Main Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* Basic Info Cards */}
                      <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700 hover:border-blue-500/30 transition-colors">
                        <div className="flex items-start space-x-3">
                          <Network className="text-blue-400 mt-1" size={24} />
                          <div>
                            <p className="text-sm text-gray-400">Client IP</p>
                            <p className="text-xl font-mono font-semibold text-white">
                              {results.data.client_ip}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Your connection address</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700 hover:border-blue-500/30 transition-colors">
                        <div className="flex items-start space-x-3">
                          <Monitor className="text-purple-400 mt-1" size={24} />
                          <div>
                            <p className="text-sm text-gray-400">Operating System</p>
                            <p className="text-xl font-semibold text-white">
                              {results.data.os}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Detected from User-Agent</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700 hover:border-blue-500/30 transition-colors">
                        <div className="flex items-start space-x-3">
                          <Globe className="text-emerald-400 mt-1" size={24} />
                          <div>
                            <p className="text-sm text-gray-400">Browser</p>
                            <p className="text-xl font-semibold text-white">
                              {results.data.browser}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">User-Agent detection</p>
                          </div>
                        </div>
                      </div>

                     {/* Geo Info Section */}
                     {results.data?.geo_info && ( // Optional chaining to avoid TypeError
                       <>
                         {results.data.geo_info.status === 'success' ? (
                           <>
                             <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700 hover:border-blue-500/30 transition-colors">
                               <div className="flex items-start space-x-3">
                                 <MapPin className="text-yellow-400 mt-1" size={24} />
                                 <div>
                                   <p className="text-sm text-gray-400">Location</p>
                                   <p className="text-xl font-semibold text-white">
                                     {results.data.geo_info.city}, {results.data.geo_info.country}
                                   </p>
                                   <p className="text-xs text-gray-500 mt-1">
                                     {results.data.geo_info.regionName}
                                   </p>
                                 </div>
                               </div>
                             </div>
                     
                             <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700 hover:border-blue-500/30 transition-colors">
                               <div className="flex items-start space-x-3">
                                 <Wifi className="text-red-400 mt-1" size={24} />
                                 <div>
                                   <p className="text-sm text-gray-400">ISP</p>
                                   <p className="text-xl font-semibold text-white">
                                     {results.data.geo_info.isp}
                                   </p>
                                   <p className="text-xs text-gray-500 mt-1">
                                     AS{results.data.geo_info.as}
                                   </p>
                                 </div>
                               </div>
                             </div>
                     
                             <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700 hover:border-blue-500/30 transition-colors">
                               <div className="flex items-start space-x-3">
                                 <Map className="text-blue-400 mt-1" size={24} />
                                 <div>
                                   <p className="text-sm text-gray-400">Coordinates</p>
                                   <p className="text-xl font-mono font-semibold text-white">
                                     {results.data.geo_info.lat}, {results.data.geo_info.lon}
                                   </p>
                                   <p className="text-xs text-gray-500 mt-1">
                                     Timezone: {results.data.geo_info.timezone}
                                   </p>
                                 </div>
                               </div>
                             </div>
                           </>
                         ) : (
                           <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700 hover:border-blue-500/30 transition-colors col-span-2">
                             <div className="flex items-start space-x-3">
                               <AlertTriangle className="text-yellow-400 mt-1" size={24} />
                               <div>
                                 <p className="text-sm text-gray-400">Geolocation</p>
                                 <p className="text-xl font-semibold text-white">
                                   {results.data.geo_info.message || 'Location unavailable'}
                                 </p>
                                 <p className="text-xs text-gray-500 mt-1">
                                   {results.data.geo_info.query && `IP: ${results.data.geo_info.query}`}
                                 </p>
                               </div>
                             </div>
                           </div>
                         )}
                       </>
                     )}

                      {/* Language Info */}
                      <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700 hover:border-blue-500/30 transition-colors">
                        <div className="flex items-start space-x-3">
                          <Languages className="text-purple-400 mt-1" size={24} />
                          <div>
                            <p className="text-sm text-gray-400">Language Preferences</p>
                            <p className="text-xl font-mono font-semibold text-white">
                              {results.data.accept_language}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">From Accept-Language header</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                  </div>

                  {/* Footer Actions */}
                  <div className="border-t border-gray-700 p-4 bg-gray-800 bg-opacity-60 flex justify-end space-x-3">
                    <button className="px-4 py-2 text-sm rounded-lg border border-gray-600 hover:bg-gray-700 transition-colors">
                      Save Results
                    </button>
                    <button className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors">
                      Export Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default NetworkDiagnostics;
